import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import throttle from 'lodash/throttle';
import differenceInDays from 'date-fns/differenceInDays';
import intervalToDuration from 'date-fns/intervalToDuration';
import isBefore from 'date-fns/isBefore';

export { useLocalStorage } from './useLocalStorage';
export { useSessionStorage } from './useSessionStorage';

export const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

export const useRefInView = (initialState = false) => {
  const [refInView, setRefInView] = useState(initialState);
  const triggerRef = useRef(null);

  useEffect(() => {
    const onViewportChange = () => {
      if (triggerRef.current) {
        const { top, bottom } = triggerRef.current.getBoundingClientRect();

        if (top >= 0 && bottom <= window.innerHeight) {
          setRefInView(true);
        } else {
          setRefInView(false);
        }
      }
    };

    const throttledOnViewportChange = throttle(onViewportChange, 100);
    onViewportChange();
    window.addEventListener('scroll', throttledOnViewportChange);
    window.addEventListener('resize', throttledOnViewportChange);
    return () => {
      window.removeEventListener('scroll', throttledOnViewportChange);
      window.removeEventListener('resize', throttledOnViewportChange);
    };
  }, []);

  return [refInView, triggerRef];
};

const getDateTimeDifference = targetDate => {
  const now = new Date();
  const days = Math.max(differenceInDays(targetDate, now), 0);
  // TODO eventually extract `days` from intervalToDuration but figure out a way
  // to deal with days overlapping into the months property and starting back at 1.
  let hours = 0,
    minutes = 0,
    seconds = 0;
  if (isBefore(now, targetDate)) {
    ({ hours, minutes, seconds } = intervalToDuration({
      start: now,
      end: targetDate,
    }));
  }

  return { days, hours, minutes, seconds };
};

export const useCountdown = targetDate => {
  const [countdown, setCountdown] = useState(getDateTimeDifference(targetDate));

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      if (isBefore(now, targetDate)) {
        setCountdown(getDateTimeDifference(targetDate));
      } else {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  return countdown;
};

export const sideScroll = (columns, page, element, speed, distance, step) => {
  switch (page) {
    case 0:
      step = 950;
      if (element.scrollLeft > 30) {
        step = step * 10 * -1;
      }
      break;
    case 1:
      step = 20;
      if (columns.length > 5) {
        step = 250;
      }
      if (element.scrollLeft > 90) {
        step = step * -1;
      }
      break;
    case 2:
      step = 900;
      if (element.scrollLeft === 0) {
        step = distance * 4;
      }
      break;
    default:
      step = 0;
  }
  let scrollAmount = 0;
  const slideTimer = setInterval(() => {
    element.scrollLeft += step;
    scrollAmount += Math.abs(step);
    if (scrollAmount >= distance) {
      clearInterval(slideTimer);
    }
  }, speed);
};
